import * as React from 'react';
import { withStyles, WithStyles, createStyles, Theme } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Select from '@material-ui/core/Select';

import { CURRENCY_OPTIONS } from 'ha/constants/Currencies';
import LazySlider from 'ha/components/LazySlider';
import formatPrice from 'ha/utils/formatPrice';
import units from 'ha/theme/units';

import Row from 'ha/ui/next/Row';

const styles = (t: Theme) =>
  createStyles({
    currencyContainer: {
      width: units.rem(5),
    },
    controls: {
      marginBottom: units.rem(1),
    },
    priceLabel: {
      paddingTop: units.rem(0.5),
    },
    container: {
      position: 'relative',
      padding: units.trbl(0, 1, 0.5, 0),
      margin: 0,
    },
    distribution: {
      height: units.rem(2.7),
      width: '100%',
      position: 'relative',
    },
    distributionItem: {
      height: '100%',
      borderTopRightRadius: units.rem(0.1),
      borderTopLeftRadius: units.rem(0.1),
      background: t.palette.grey[300],
    },
  });

interface DistributionStep {
  range: number;
  value: number;
}

const renderDistributionBar = (
  step: DistributionStep,
  index: number,
  distributionMaxValue: number,
  barsCount: number,
  className: string,
): React.ReactNode => (
  <div
    key={index}
    title={step.value.toString()}
    style={{
      width: `${100 / barsCount}%`,
      height: `${(step.value / distributionMaxValue) * 100}%`,
      position: 'absolute',
      bottom: 0,
      left: `${(index * 100) / barsCount}%`,
    }}
  >
    <div className={className} />
  </div>
);

type PriceChangeCallback = ({
  currency,
  max,
  min,
}: {
  currency: string;
  max?: number;
  min?: number;
}) => void;

interface Props {
  rate: number;
  currency: string;
  priceDistribution: Array<{ range: number; value: number }>;
  priceMax: number;
  priceMin: number;
  selectedMin: number;
  selectedMax: number;
  onPriceChange: PriceChangeCallback;
  onCurrencyChange: PriceChangeCallback;
  priceLabelFormatter?: (price: number) => React.ReactNode;
  priceFilterTitle: string;
}

export class PriceRangeFilter extends React.Component<
  Props & WithStyles<typeof styles>
> {
  onSliderChange = ([min, max]: number[]): void => {
    const { currency, onPriceChange } = this.props;

    onPriceChange({ currency, min, max });
  };

  handleCurrencyChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    const { onCurrencyChange, selectedMin, selectedMax } = this.props;

    onCurrencyChange({
      currency: event.currentTarget.value,
      min: selectedMin,
      max: selectedMax,
    });
  };

  priceLabelFormatter = (price: number): React.ReactNode => {
    const { currency, rate, priceLabelFormatter } = this.props;

    if (priceLabelFormatter) {
      return priceLabelFormatter(price);
    } else {
      return formatPrice(price * rate, currency);
    }
  };

  render() {
    const {
      classes,
      priceDistribution,
      priceMax,
      priceMin,
      currency,
      selectedMin,
      selectedMax,
      priceFilterTitle,
    } = this.props;

    const distributionValues = priceDistribution.map(step => step.value);
    const distributionMaxValue = distributionValues.reduce(
      (max, current) => Math.max(max, current),
      0,
    );

    const disabled = priceMin === priceMax;

    return (
      <Row direction="column">
        <Row justify="space-between" className={classes.controls}>
          <Typography variant="subheading">{priceFilterTitle}</Typography>

          <div className={classes.currencyContainer}>
            <Select
              native
              value={currency}
              onChange={this.handleCurrencyChange}
            >
              {CURRENCY_OPTIONS}
            </Select>
          </div>
        </Row>

        <div className={classes.container}>
          {priceDistribution.length ? (
            <Row justify="center">
              <div className={classes.distribution}>
                {priceDistribution.map((step, index) =>
                  renderDistributionBar(
                    step,
                    index,
                    distributionMaxValue,
                    priceDistribution.length,
                    classes.distributionItem,
                  ),
                )}
              </div>
            </Row>
          ) : null}

          <Row
            justify="center"
            className={priceDistribution.length ? '' : 'm-t-1'}
          >
            <LazySlider
              value={[selectedMin, selectedMax]}
              min={priceMin}
              max={priceMax}
              disabled={disabled}
              tipFormatter={this.priceLabelFormatter}
              onAfterChange={this.onSliderChange}
              isRange
            />
          </Row>

          <Row justify="space-between" className={classes.priceLabel}>
            <Typography variant="caption">
              {this.priceLabelFormatter(priceMin)}
            </Typography>
            {!disabled && (
              <Typography variant="caption">
                {this.priceLabelFormatter(priceMax)}
              </Typography>
            )}
          </Row>
        </div>
      </Row>
    );
  }
}

export default withStyles(styles)(PriceRangeFilter);
